import { TypeManagerDecorator } from "../../../main/type.map.service";
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnChanges,
    SimpleChanges,
    ViewEncapsulation,
    inject,
    signal,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { CommonModule } from "@angular/common";
import { S25MultiselectModule } from "../../s25-multiselect/s25-multiselect.module";
import { S25DropdownPaginatedModule } from "../../s25-dropdown/s25.dropdown.paginated.module";
import type { ReloadAndSelectParams } from "../../s25-dropdown/single-select/s25.search.dropdown.component";
import { S25ItemI } from "../../../pojo/S25ItemI";
import { Item } from "../../../pojo/Item";
import { DropDownItem } from "../../../pojo/DropDownItem";
import {
    DataIngestionService,
    IngestionPointAccessRule,
    IngestionPointAccessRuleObject,
    IngestionPointAccessRulesUpdateEntry,
} from "../data.ingestion.service";
import { IngestionAccess } from "../ingestion.access";
import { AlertService } from "../../../services/alert.service";
import { S25Util } from "../../../util/s25-util";

/** Actions that may be stored on access rules (owner-only actions excluded), grouped for the UI. */
type AccessRuleActionRow = {
    action: IngestionAccess.IngestionPointAction;
    label: string;
};

type AccessRuleActionGroup = {
    groupLabel: string;
    rows: AccessRuleActionRow[];
};

const RULE_ACTION_GROUPS: AccessRuleActionGroup[] = [
    {
        groupLabel: "Form use",
        rows: [
            { action: IngestionAccess.IngestionPointAction.FILL, label: "Fill" },
            { action: IngestionAccess.IngestionPointAction.COPY, label: "Copy" },
        ],
    },
    {
        groupLabel: "Edit setup",
        rows: [
            { action: IngestionAccess.IngestionPointAction.EDIT_SCHEMA, label: "Edit form" },
            // { action: IngestionAccess.IngestionPointAction.EDIT_WORKFLOW, label: "Edit workflow" }, //hiding to reduce testing scope
            { action: IngestionAccess.IngestionPointAction.EDIT_SETTINGS, label: "Edit security" },
        ],
    },
    {
        groupLabel: "Submissions",
        rows: [
            { action: IngestionAccess.IngestionPointAction.VIEW_ALL_SUBMISSIONS, label: "View all submissions" },
            { action: IngestionAccess.IngestionPointAction.DISTRIBUTE, label: "Distribute" },
        ],
    },
];

const PRINCIPAL_OPTIONS: { value: string; label: string }[] = [
    { value: IngestionAccess.RulePrincipal.PUBLIC, label: "Anyone (not signed in)" },
    { value: IngestionAccess.RulePrincipal.LOGGED_IN, label: "Any signed-in user" },
    { value: IngestionAccess.RulePrincipal.CONTACTS, label: "Specific contacts" },
    { value: IngestionAccess.RulePrincipal.SECURITY_GROUPS, label: "Security groups" },
    { value: IngestionAccess.RulePrincipal.ORG_ROLE, label: "Contact roles on organization(s)" },
    /** Hidden from the principal dropdown for now; still appended when a loaded rule already uses it. */
    { value: IngestionAccess.RulePrincipal.ORG_RIGHT, label: "Organization right + level" },
];

const PRINCIPAL_OPTIONS_UI = PRINCIPAL_OPTIONS.filter((p) => p.value !== IngestionAccess.RulePrincipal.ORG_RIGHT);

const SCOPE_OPTIONS: { value: string; label: string }[] = [
    { value: IngestionAccess.RuleScope.SUB_CAT, label: "This form’s organization (sub-category)" },
    { value: IngestionAccess.RuleScope.ORGS, label: "Specific organizations (account IDs)" },
    { value: IngestionAccess.RuleScope.ORG_QUERY, label: "Organizations from saved query" },
];

function deepCloneRules(rules: IngestionPointAccessRule[] | undefined): IngestionPointAccessRule[] {
    return rules?.length ? JSON.parse(JSON.stringify(rules)) : [];
}

function newEmptyRule(): IngestionPointAccessRule {
    return {
        ruleName: "New rule",
        active: true,
        principalKind: IngestionAccess.RulePrincipal.LOGGED_IN,
        scopeKind: "",
        actions: [{ formAction: IngestionAccess.IngestionPointAction.FILL }],
        objects: [],
    };
}

@TypeManagerDecorator("s25-ng-ingestion-admin-access-rules")
@Component({
    selector: "s25-ng-ingestion-admin-access-rules",
    standalone: true,
    imports: [CommonModule, FormsModule, S25MultiselectModule, S25DropdownPaginatedModule],
    template: `
        <div class="access-rules-root">
            @if (loadError()) {
                <div class="access-rules-banner access-rules-banner--error" role="alert">
                    {{ loadError() }}
                </div>
            }
            @if (loading()) {
                <p class="access-rules-muted">Loading access rules…</p>
            } @else if (!loadError()) {
                <p class="access-rules-intro">
                    Owner and institution admins always have full access. Others are denied unless a rule below grants
                    an action (OR — first match wins).
                </p>

                @if (saveError()) {
                    <div class="access-rules-banner access-rules-banner--error" role="alert">{{ saveError() }}</div>
                }
                @if (saveOk()) {
                    <div class="access-rules-banner access-rules-banner--ok" role="status">Access rules saved.</div>
                }

                @if (!draftRules.length) {
                    <p class="access-rules-muted">
                        No rules are defined yet. Anyone who is not the owner or an admin will have no rule-based access
                        until you add at least one rule.
                    </p>
                }

                @for (rule of draftRules; track $index) {
                    <div class="access-rule-card">
                        <div class="access-rule-card__header">
                            <label class="access-rule-field access-rule-field--grow">
                                <span class="access-rule-label">Rule name</span>
                                <input
                                    type="text"
                                    class="c-input"
                                    [(ngModel)]="rule.ruleName"
                                    [ngModelOptions]="{ standalone: true }"
                                />
                            </label>
                            <label class="access-rule-inline">
                                <input
                                    type="checkbox"
                                    [ngModel]="rule.active !== false"
                                    (ngModelChange)="rule.active = $event"
                                    [ngModelOptions]="{ standalone: true }"
                                />
                                Active
                            </label>
                            <div class="access-rule-card__header-actions">
                                <button
                                    type="button"
                                    class="aw-button aw-button--danger--outline"
                                    (click)="removeRuleAt($index)"
                                    [disabled]="saving()"
                                >
                                    Remove rule
                                </button>
                            </div>
                        </div>

                        <div class="access-rule-grid">
                            <label class="access-rule-field">
                                <span class="access-rule-label">Who (principal)</span>
                                <select
                                    class="cn-form__control"
                                    [ngModel]="rule.principalKind"
                                    (ngModelChange)="onPrincipalChange(rule, $event)"
                                    [ngModelOptions]="{ standalone: true }"
                                >
                                    @for (p of principalOptionsFor(rule); track p.value) {
                                        <option [value]="p.value">{{ p.label }}</option>
                                    }
                                </select>
                            </label>

                            @if (needsScope(rule)) {
                                <label class="access-rule-field">
                                    <span class="access-rule-label">Organization scope</span>
                                    <select
                                        class="cn-form__control"
                                        [ngModel]="rule.scopeKind || ''"
                                        (ngModelChange)="onScopeChange(rule, $event)"
                                        [ngModelOptions]="{ standalone: true }"
                                    >
                                        <option value="">— Select —</option>
                                        @for (s of scopeOptions; track s.value) {
                                            <option [value]="s.value">{{ s.label }}</option>
                                        }
                                    </select>
                                </label>
                            }

                            @if (needsOrgQueryId(rule)) {
                                <div class="access-rule-field access-rule-org-query">
                                    <span class="access-rule-label">Organization saved search</span>
                                    <s25-ng-search-dropdown
                                        class="access-rule-org-query__dropdown"
                                        [itemTypeId]="organizationSearchItemTypeId"
                                        [allowNonQueryId]="false"
                                        [placeholder]="'Select a saved organization search'"
                                        [initialSearch]="orgQueryInitialSearch(rule)"
                                        (valueChange)="onOrgQuerySearchChange(rule, $event)"
                                    />
                                </div>
                            }
                        </div>

                        @if (isOrgRight(rule)) {
                            <div class="access-rule-grid access-rule-subsection">
                                <label class="access-rule-field">
                                    <span class="access-rule-label">Right name</span>
                                    <input
                                        type="text"
                                        class="c-input"
                                        [(ngModel)]="rule.orgRight!.rightName"
                                        placeholder="e.g. STUDENT_ENGAGEMENT"
                                        [ngModelOptions]="{ standalone: true }"
                                    />
                                </label>
                                <label class="access-rule-field">
                                    <span class="access-rule-label">Minimum access</span>
                                    <select
                                        class="cn-form__control"
                                        [(ngModel)]="rule.orgRight!.minAccessLevel"
                                        [ngModelOptions]="{ standalone: true }"
                                    >
                                        @for (lvl of accessLevels; track lvl) {
                                            <option [value]="lvl">{{ lvl }}</option>
                                        }
                                    </select>
                                </label>
                            </div>
                            <p class="access-rules-hint">
                                N = None, R = Read, C = Create/Update, F = Full. Users must hold the right at this level
                                (or higher) on each organization in scope.
                            </p>
                        }

                        <fieldset class="access-rule-fieldset">
                            <legend class="access-rule-label">Actions granted by this rule</legend>
                            @for (grp of ruleActionGroups; track grp.groupLabel) {
                                <div class="access-rule-action-group">
                                    <div class="access-rule-action-group__title">{{ grp.groupLabel }}</div>
                                    <div class="access-rule-actions">
                                        @for (row of grp.rows; track row.action) {
                                            <label class="access-rule-inline">
                                                <input
                                                    type="checkbox"
                                                    [checked]="hasAction(rule, row.action)"
                                                    (change)="
                                                        toggleAction(rule, row.action, $any($event.target).checked)
                                                    "
                                                />
                                                {{ row.label }}
                                            </label>
                                        }
                                    </div>
                                </div>
                            }
                        </fieldset>

                        @if (needsObjects(rule)) {
                            <fieldset class="access-rule-fieldset">
                                <legend class="access-rule-label">{{ objectSectionTitle(rule) }}</legend>
                                @switch ((rule.principalKind || "").toUpperCase()) {
                                    @case (PrincipalKind.CONTACTS) {
                                        <div class="access-rule-multiselect">
                                            <s25-ng-multiselect-search-criteria
                                                [type]="'contacts'"
                                                [selectedItems]="objectsToItems(rule, accessRuleObjectType.CONT_ID)"
                                                (changed)="
                                                    onAccessObjectsChange(rule, accessRuleObjectType.CONT_ID, $event)
                                                "
                                                [showResults]="true"
                                                [popoverOnBody]="true"
                                            />
                                        </div>
                                    }
                                    @case (PrincipalKind.SECURITY_GROUPS) {
                                        <div class="access-rule-multiselect">
                                            <s25-ng-multiselect-search-criteria
                                                [type]="'securityGroups'"
                                                [selectedItems]="
                                                    objectsToItems(rule, accessRuleObjectType.SEC_GROUP_ID)
                                                "
                                                (changed)="
                                                    onAccessObjectsChange(
                                                        rule,
                                                        accessRuleObjectType.SEC_GROUP_ID,
                                                        $event
                                                    )
                                                "
                                                [showResults]="true"
                                                [popoverOnBody]="true"
                                            />
                                        </div>
                                    }
                                    @case (PrincipalKind.ORG_ROLE) {
                                        @if ((rule.scopeKind || "").toUpperCase() === ScopeKind.ORGS) {
                                            <div class="access-rule-field access-rule-multiselect">
                                                <span class="access-rule-label">Contact roles</span>
                                                <s25-ng-multiselect-search-criteria
                                                    [type]="'organizationRoles'"
                                                    [selectedItems]="
                                                        objectsToItems(rule, accessRuleObjectType.CONT_ROLE_ID)
                                                    "
                                                    (changed)="onOrgRoleOrgsRolesChange(rule, $event)"
                                                    [showResults]="true"
                                                    [popoverOnBody]="true"
                                                />
                                            </div>
                                            <div class="access-rule-field access-rule-multiselect">
                                                <span class="access-rule-label">Organizations</span>
                                                <s25-ng-multiselect-search-criteria
                                                    [type]="'organizations'"
                                                    [selectedItems]="
                                                        objectsToItems(rule, accessRuleObjectType.ACCOUNT_ID)
                                                    "
                                                    (changed)="onOrgRoleOrgsAccountsChange(rule, $event)"
                                                    [showResults]="true"
                                                    [popoverOnBody]="true"
                                                />
                                            </div>
                                        } @else {
                                            <div class="access-rule-multiselect">
                                                <s25-ng-multiselect-search-criteria
                                                    [type]="'organizationRoles'"
                                                    [selectedItems]="
                                                        objectsToItems(rule, accessRuleObjectType.CONT_ROLE_ID)
                                                    "
                                                    (changed)="
                                                        onAccessObjectsChange(
                                                            rule,
                                                            accessRuleObjectType.CONT_ROLE_ID,
                                                            $event
                                                        )
                                                    "
                                                    [showResults]="true"
                                                    [popoverOnBody]="true"
                                                />
                                            </div>
                                        }
                                    }
                                    @case (PrincipalKind.ORG_RIGHT) {
                                        <div class="access-rule-multiselect">
                                            <s25-ng-multiselect-search-criteria
                                                [type]="'organizations'"
                                                [selectedItems]="objectsToItems(rule, accessRuleObjectType.ACCOUNT_ID)"
                                                (changed)="
                                                    onAccessObjectsChange(rule, accessRuleObjectType.ACCOUNT_ID, $event)
                                                "
                                                [showResults]="true"
                                                [popoverOnBody]="true"
                                            />
                                        </div>
                                    }
                                }
                            </fieldset>
                        }
                    </div>
                }

                <div class="access-rules-toolbar">
                    <button
                        type="button"
                        class="aw-button aw-button--outline"
                        (click)="addRule()"
                        [disabled]="saving()"
                    >
                        Add rule
                    </button>
                    <button type="button" class="aw-button aw-button--outline" (click)="reload()" [disabled]="saving()">
                        Reload
                    </button>
                </div>
            }
        </div>
    `,
    styles: `
        .access-rules-root {
            max-width: 960px;
        }
        .access-rules-intro {
            color: var(--color--text-muted, #555);
            margin: 0 0 1rem;
            line-height: 1.45;
        }
        .access-rules-muted {
            color: var(--color--text-muted, #666);
        }
        .access-rules-hint {
            font-size: 0.85rem;
            color: var(--color--text-muted, #666);
            margin: 0 0 0.75rem;
        }
        .access-rules-banner {
            padding: 0.6rem 0.75rem;
            border-radius: 6px;
            margin-bottom: 0.75rem;
        }
        .access-rules-banner--error {
            background: #fdecea;
            border: 1px solid #f5c2c0;
        }
        .access-rules-banner--ok {
            background: #e8f5e9;
            border: 1px solid #c8e6c9;
        }
        .access-rule-card {
            border: 1px solid var(--color--border, #ddd);
            border-radius: 8px;
            padding: 1rem 1.1rem;
            margin-bottom: 1rem;
            background: var(--color--surface-bg, #fff);
        }
        .access-rule-card__header {
            display: flex;
            flex-wrap: wrap;
            gap: 0.75rem 1rem;
            align-items: flex-end;
            margin-bottom: 0.75rem;
        }
        .access-rule-card__header-actions {
            flex: 0 0 auto;
            margin-left: auto;
        }
        .access-rule-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
            gap: 0.75rem 1rem;
            margin-bottom: 0.75rem;
        }
        .access-rule-field {
            display: flex;
            flex-direction: column;
            gap: 0.25rem;
            min-width: 0;
        }
        .access-rule-field--grow {
            flex: 1 1 200px;
        }
        .access-rule-label {
            font-size: 0.8rem;
            font-weight: 600;
            color: var(--color--text-muted, #555);
        }
        .access-rule-inline {
            display: inline-flex;
            align-items: center;
            gap: 0.35rem;
            white-space: nowrap;
        }
        .access-rule-fieldset {
            border: 0;
            padding: 0;
            margin: 0.5rem 0 0;
        }
        .access-rule-fieldset legend {
            padding: 0;
            margin-bottom: 0.35rem;
        }
        .access-rule-action-group {
            margin-bottom: 0.85rem;
        }
        .access-rule-action-group:last-child {
            margin-bottom: 0;
        }
        .access-rule-action-group__title {
            font-size: 0.8rem;
            font-weight: 600;
            color: var(--color--text-muted, #555);
            margin-bottom: 0.35rem;
        }
        .access-rule-actions {
            display: flex;
            flex-wrap: wrap;
            gap: 0.5rem 1rem;
        }
        .access-rule-multiselect {
            margin-bottom: 0.75rem;
            min-width: 0;
        }
        .access-rule-org-query__dropdown {
            display: block;
            max-width: 100%;
        }
        .access-rule-subsection {
            margin-top: 0.25rem;
        }
        .access-rules-toolbar {
            display: flex;
            flex-wrap: wrap;
            gap: 0.5rem;
            margin-top: 0.5rem;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IngestionAdminAccessRulesComponent implements OnChanges {
    @Input({ required: true }) ingestionPointId!: number;

    private readonly cd = inject(ChangeDetectorRef);

    readonly loading = signal(false);
    readonly saving = signal(false);
    readonly loadError = signal<string | null>(null);
    readonly saveError = signal<string | null>(null);
    readonly saveOk = signal(false);

    draftRules: IngestionPointAccessRule[] = [];

    principalOptionsFor(rule: IngestionPointAccessRule): { value: string; label: string }[] {
        if ((rule.principalKind || "").toUpperCase() === IngestionAccess.RulePrincipal.ORG_RIGHT) {
            const orgRight = PRINCIPAL_OPTIONS.find((p) => p.value === IngestionAccess.RulePrincipal.ORG_RIGHT);
            return orgRight ? [...PRINCIPAL_OPTIONS_UI, orgRight] : PRINCIPAL_OPTIONS_UI;
        }
        return PRINCIPAL_OPTIONS_UI;
    }
    readonly scopeOptions = SCOPE_OPTIONS;
    readonly ruleActionGroups = RULE_ACTION_GROUPS;
    readonly accessLevels = IngestionAccess.ORG_RIGHT_MIN_LEVELS;
    /** Exposed for template @case / comparisons (wire principalKind values). */
    readonly PrincipalKind = IngestionAccess.RulePrincipal;
    /** Exposed for template @case / comparisons (wire scopeKind values). */
    readonly ScopeKind = IngestionAccess.RuleScope;
    readonly accessRuleObjectType = IngestionAccess.AccessRuleObjectType;
    readonly organizationSearchItemTypeId = Item.Ids.Organization;

    ngOnChanges(changes: SimpleChanges): void {
        if (changes["ingestionPointId"] && this.ingestionPointId != null) {
            void this.reload();
        }
    }

    needsScope(rule: IngestionPointAccessRule): boolean {
        const pk = (rule.principalKind || "").toUpperCase();
        return pk === IngestionAccess.RulePrincipal.ORG_ROLE || pk === IngestionAccess.RulePrincipal.ORG_RIGHT;
    }

    needsOrgQueryId(rule: IngestionPointAccessRule): boolean {
        return this.needsScope(rule) && (rule.scopeKind || "").toUpperCase() === IngestionAccess.RuleScope.ORG_QUERY;
    }

    orgQueryInitialSearch(rule: IngestionPointAccessRule): ReloadAndSelectParams | undefined {
        const id = rule.orgQueryId;
        if (id == null) {
            return undefined;
        }
        return {
            itemTypeId: Item.Ids.Organization,
            property: "itemId",
            value: id,
        };
    }

    onOrgQuerySearchChange(rule: IngestionPointAccessRule, item: DropDownItem | null | undefined): void {
        const raw = item?.itemId ?? item?.val;
        rule.orgQueryId = raw != null && raw !== "" && !Number.isNaN(Number(raw)) ? Number(raw) : undefined;
        this.clearBanners();
        this.cd.markForCheck();
    }

    isOrgRight(rule: IngestionPointAccessRule): boolean {
        return (rule.principalKind || "").toUpperCase() === IngestionAccess.RulePrincipal.ORG_RIGHT;
    }

    needsObjects(rule: IngestionPointAccessRule): boolean {
        const pk = (rule.principalKind || "").toUpperCase();
        if (pk === IngestionAccess.RulePrincipal.ORG_RIGHT) {
            return (rule.scopeKind || "").toUpperCase() === IngestionAccess.RuleScope.ORGS;
        }
        return (
            pk === IngestionAccess.RulePrincipal.CONTACTS ||
            pk === IngestionAccess.RulePrincipal.SECURITY_GROUPS ||
            pk === IngestionAccess.RulePrincipal.ORG_ROLE
        );
    }

    objectSectionTitle(rule: IngestionPointAccessRule): string {
        const pk = (rule.principalKind || "").toUpperCase();
        switch (pk) {
            case IngestionAccess.RulePrincipal.CONTACTS:
                return "Contacts — search and select";
            case IngestionAccess.RulePrincipal.SECURITY_GROUPS:
                return "Security groups — search and select";
            case IngestionAccess.RulePrincipal.ORG_ROLE:
                return (rule.scopeKind || "").toUpperCase() === IngestionAccess.RuleScope.ORGS
                    ? "Roles and organizations — search and select below"
                    : "Contact roles — search and select";
            case IngestionAccess.RulePrincipal.ORG_RIGHT:
                return "Organizations in scope — search and select";
            default:
                return "Targets";
        }
    }

    objectsToItems(rule: IngestionPointAccessRule, objectType: string): S25ItemI[] {
        const want = (objectType || "").toLowerCase();
        const rows = rule.objects?.filter((o) => (o.objectType || "").toLowerCase() === want && o.objectId != null);
        if (!rows?.length) {
            return [];
        }
        return rows.map((o) => ({
            itemId: o.objectId as number,
            itemName: o.objectName,
        }));
    }

    onAccessObjectsChange(rule: IngestionPointAccessRule, objectType: string, items: S25ItemI[]): void {
        rule.objects = this.itemsToObjects(items, objectType);
        this.clearBanners();
        this.cd.markForCheck();
    }

    onOrgRoleOrgsRolesChange(rule: IngestionPointAccessRule, items: S25ItemI[]): void {
        const orgPart = (rule.objects || []).filter(
            (o) => (o.objectType || "").toLowerCase() === IngestionAccess.AccessRuleObjectType.ACCOUNT_ID,
        );
        rule.objects = [...this.itemsToObjects(items, IngestionAccess.AccessRuleObjectType.CONT_ROLE_ID), ...orgPart];
        this.clearBanners();
        this.cd.markForCheck();
    }

    onOrgRoleOrgsAccountsChange(rule: IngestionPointAccessRule, items: S25ItemI[]): void {
        const rolePart = (rule.objects || []).filter(
            (o) => (o.objectType || "").toLowerCase() === IngestionAccess.AccessRuleObjectType.CONT_ROLE_ID,
        );
        rule.objects = [...rolePart, ...this.itemsToObjects(items, IngestionAccess.AccessRuleObjectType.ACCOUNT_ID)];
        this.clearBanners();
        this.cd.markForCheck();
    }

    private itemsToObjects(items: S25ItemI[] | undefined, objectType: string): IngestionPointAccessRuleObject[] {
        const out: IngestionPointAccessRuleObject[] = [];
        for (const item of items || []) {
            const raw = item.itemId ?? item.val;
            const id = typeof raw === "string" ? Number.parseInt(raw, 10) : Number(raw);
            if (id != null && !Number.isNaN(id)) {
                out.push({ objectType, objectId: id });
            }
        }
        return out;
    }

    onPrincipalChange(rule: IngestionPointAccessRule, value: string): void {
        rule.principalKind = value;
        const pk = value.toUpperCase();
        switch (pk) {
            case IngestionAccess.RulePrincipal.PUBLIC:
            case IngestionAccess.RulePrincipal.LOGGED_IN:
                rule.scopeKind = "";
                rule.orgQueryId = undefined;
                rule.objects = [];
                rule.orgRight = undefined;
                break;
            case IngestionAccess.RulePrincipal.CONTACTS:
            case IngestionAccess.RulePrincipal.SECURITY_GROUPS:
                rule.scopeKind = "";
                rule.orgQueryId = undefined;
                rule.orgRight = undefined;
                rule.objects = rule.objects ?? [];
                break;
            case IngestionAccess.RulePrincipal.ORG_ROLE:
                rule.orgRight = undefined;
                if (!rule.scopeKind) {
                    rule.scopeKind = IngestionAccess.RuleScope.SUB_CAT;
                }
                rule.objects = rule.objects ?? [];
                break;
            case IngestionAccess.RulePrincipal.ORG_RIGHT:
                if (!rule.scopeKind) {
                    rule.scopeKind = IngestionAccess.RuleScope.SUB_CAT;
                }
                if (!rule.orgRight) {
                    rule.orgRight = {
                        rightName: "",
                        minAccessLevel: IngestionAccess.DEFAULT_ORG_RIGHT_MIN_LEVEL,
                    };
                }
                if ((rule.scopeKind || "").toUpperCase() !== IngestionAccess.RuleScope.ORGS) {
                    rule.objects = [];
                } else {
                    rule.objects = rule.objects ?? [];
                }
                break;
            default:
                break;
        }
        this.clearBanners();
        this.cd.markForCheck();
    }

    onScopeChange(rule: IngestionPointAccessRule, value: string): void {
        rule.scopeKind = value || "";
        if ((value || "").toUpperCase() !== IngestionAccess.RuleScope.ORG_QUERY) {
            rule.orgQueryId = undefined;
        }
        const pk = (rule.principalKind || "").toUpperCase();
        if (pk === IngestionAccess.RulePrincipal.ORG_RIGHT) {
            if ((value || "").toUpperCase() !== IngestionAccess.RuleScope.ORGS) {
                rule.objects = [];
            } else {
                rule.objects = rule.objects ?? [];
            }
        }
        if (pk === IngestionAccess.RulePrincipal.ORG_ROLE) {
            const sk = (value || "").toUpperCase();
            if (sk !== IngestionAccess.RuleScope.ORGS && rule.objects?.length) {
                rule.objects = rule.objects.filter(
                    (o) => (o.objectType || "").toLowerCase() === IngestionAccess.AccessRuleObjectType.CONT_ROLE_ID,
                );
            }
            rule.objects = rule.objects ?? [];
        }
        this.clearBanners();
        this.cd.markForCheck();
    }

    hasAction(rule: IngestionPointAccessRule, action: IngestionAccess.IngestionPointAction): boolean {
        const want = String(action).toUpperCase();
        const list = rule.actions || [];
        return list.some((a) => (a.formAction || "").toUpperCase() === want);
    }

    toggleAction(rule: IngestionPointAccessRule, action: IngestionAccess.IngestionPointAction, on: boolean): void {
        if (!rule.actions) {
            rule.actions = [];
        }
        const name = action as string;
        if (on) {
            if (!this.hasAction(rule, action)) {
                rule.actions.push({ formAction: name });
            }
        } else {
            rule.actions = rule.actions.filter((a) => (a.formAction || "").toUpperCase() !== name);
        }
        this.clearBanners();
        this.cd.markForCheck();
    }

    addRule(): void {
        this.draftRules.push(newEmptyRule());
        this.clearBanners();
        this.cd.markForCheck();
    }

    async removeRuleAt(ruleIndex: number): Promise<void> {
        if (ruleIndex < 0 || ruleIndex >= this.draftRules.length) {
            return;
        }
        const rule = this.draftRules[ruleIndex];
        const displayName = (rule.ruleName || "").trim();
        const quotedName = displayName ? `"${displayName}"` : "this rule";
        const confirmed = await AlertService.confirm(
            `Remove access rule ${quotedName} from the list? Click "Save form" to apply the change, or "Reload from server" to discard.`,
        );
        if (!confirmed) {
            return;
        }
        this.draftRules.splice(ruleIndex, 1);
        this.clearBanners();
        this.cd.markForCheck();
    }

    async reload(): Promise<void> {
        if (this.ingestionPointId == null) {
            return;
        }
        this.loading.set(true);
        this.loadError.set(null);
        this.clearSaveBanners();
        this.cd.markForCheck();
        try {
            const rows = await DataIngestionService.getAccessRulesForPoints([this.ingestionPointId]);
            const entry =
                rows.find((r) => r.ingestionPointId === this.ingestionPointId) ||
                ({ ingestionPointId: this.ingestionPointId, accessRules: [] } as IngestionPointAccessRulesUpdateEntry);
            this.draftRules = deepCloneRules(entry.accessRules);
        } catch (e) {
            this.loadError.set(S25Util.errorText(e) || "Could not load access rules.");
            this.draftRules = [];
        } finally {
            this.loading.set(false);
            this.cd.markForCheck();
        }
    }

    /**
     * Persists draft access rules (invoked by the form editor global Save). Returns false if the request failed.
     */
    async persistAccessRules(): Promise<boolean> {
        if (this.ingestionPointId == null) {
            return true;
        }
        this.saving.set(true);
        this.saveError.set(null);
        this.saveOk.set(false);
        this.cd.markForCheck();
        const payload: IngestionPointAccessRulesUpdateEntry[] = [
            {
                ingestionPointId: this.ingestionPointId,
                accessRules: this.sanitizeForSave(),
            },
        ];
        try {
            const saved = await DataIngestionService.putAccessRulesForPoints(payload);
            const entry = saved.find((r) => r.ingestionPointId === this.ingestionPointId);
            this.draftRules = deepCloneRules(entry?.accessRules);
            this.saveOk.set(true);
            setTimeout(() => {
                this.saveOk.set(false);
                this.cd.markForCheck();
            }, 4000);
            return true;
        } catch (caughtError) {
            this.saveError.set(S25Util.errorText(caughtError) || "Save failed.");
            return false;
        } finally {
            this.saving.set(false);
            this.cd.markForCheck();
        }
    }

    /** Strip UI-only empty object rows; normalize sort order. */
    private sanitizeForSave(): IngestionPointAccessRule[] {
        const out = JSON.parse(JSON.stringify(this.draftRules)) as IngestionPointAccessRule[];
        out.forEach((rule, i) => {
            rule.sortOrder = i;
            rule.ingestionPointId = this.ingestionPointId;
            if (rule.objects?.length) {
                rule.objects = rule.objects.filter((o) => o.objectType && o.objectId != null);
            }
            if (rule.actions?.length) {
                rule.actions = rule.actions.map((a) => ({
                    formAction: (a.formAction || "").trim().toUpperCase(),
                }));
            }
            const pk = (rule.principalKind || "").toUpperCase();
            if (pk !== IngestionAccess.RulePrincipal.ORG_RIGHT) {
                rule.orgRight = undefined;
            } else if (rule.orgRight) {
                rule.orgRight.rightName = (rule.orgRight.rightName || "").trim();
                rule.orgRight.minAccessLevel = (
                    rule.orgRight.minAccessLevel || IngestionAccess.DEFAULT_ORG_RIGHT_MIN_LEVEL
                )
                    .trim()
                    .toUpperCase();
            }
            rule.principalKind = pk;
            if (rule.scopeKind) {
                rule.scopeKind = rule.scopeKind.trim().toUpperCase();
            } else {
                rule.scopeKind = "";
            }
        });
        return out;
    }

    private clearBanners(): void {
        this.saveError.set(null);
        this.saveOk.set(false);
    }

    private clearSaveBanners(): void {
        this.saveError.set(null);
        this.saveOk.set(false);
    }
}
